import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import {
  ArrowLeft, Plus, Download, Zap, Save, Loader2, FileText, Palette,
  Briefcase, GraduationCap, Wrench, Award, FolderOpen, AlignLeft,
} from 'lucide-react';
import SectionEditor from './SectionEditor';
import ResumePreview from './ResumePreview';
import { DEFAULT_RESUME, TEMPLATE_STYLES, SECTION_TYPES, createEmptySection, resumeToPlainText } from './resumeModel';
import { logger } from '../../utils/logger';
import { API } from '../../utils/apiBase';


const ADDABLE_SECTIONS = [
  { type: SECTION_TYPES.SUMMARY, icon: AlignLeft, label: 'Summary' },
  { type: SECTION_TYPES.EXPERIENCE, icon: Briefcase, label: 'Experience' },
  { type: SECTION_TYPES.EDUCATION, icon: GraduationCap, label: 'Education' },
  { type: SECTION_TYPES.SKILLS, icon: Wrench, label: 'Skills' },
  { type: SECTION_TYPES.CERTIFICATIONS, icon: Award, label: 'Certifications' },
  { type: SECTION_TYPES.PROJECTS, icon: FolderOpen, label: 'Projects' },
];

export default function ResumeBuilder() {
  const navigate = useNavigate();
  const apiRef = useRef(API);
  const previewRef = useRef(null);
  const [resume, setResume] = useState({ ...DEFAULT_RESUME });
  const [templateId, setTemplateId] = useState('classic');
  const [saving, setSaving] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [targetJob, setTargetJob] = useState('');
  const [targetCompany, setTargetCompany] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [aiLoading, setAiLoading] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const updateContactField = useCallback((field, value) => {
    setResume(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateSection = useCallback((sectionId, updatedSection) => {
    setResume(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === sectionId ? updatedSection : s),
    }));
  }, []);

  const removeSection = useCallback((sectionId) => {
    setResume(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId),
    }));
  }, []);

  const addSection = useCallback((type) => {
    setResume(prev => ({
      ...prev,
      sections: [...prev.sections, createEmptySection(type)],
    }));
    setShowAddSection(false);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setResume(prev => {
        const oldIndex = prev.sections.findIndex(s => s.id === active.id);
        const newIndex = prev.sections.findIndex(s => s.id === over.id);
        return { ...prev, sections: arrayMove(prev.sections, oldIndex, newIndex) };
      });
    }
  }, []);

  const handleAIGenerate = useCallback(async (sectionId, type, extra = {}) => {
    const loadingKey = extra.itemIdx !== undefined ? `${sectionId}-${extra.itemIdx}` : sectionId;
    setAiLoading(loadingKey);
    try {
      const section = resume.sections.find(s => s.id === sectionId);
      const context = {};

      if (type === 'summary') {
        context.current = section?.content || '';
        const expSection = resume.sections.find(s => s.type === 'experience');
        if (expSection?.items?.[0]) {
          context.experience = expSection.items.map(i => `${i.title} at ${i.company}`).filter(s => s !== ' at ').join(', ');
        }
      } else if (type === 'bullets') {
        context.title = extra.title || '';
        context.company = extra.company || '';
        context.existing = extra.existing || '';
      } else if (type === 'skills') {
        context.current = (section?.items || []).filter(Boolean).join(', ');
      }

      const res = await axios.post(`${apiRef.current}/builder/ai-generate`, {
        section_type: type,
        context,
        target_job: targetJob,
      }, { withCredentials: true });

      setResume(prev => {
        const sections = prev.sections.map(s => {
          if (s.id !== sectionId) return s;
          if (type === 'summary' && res.data.summary) {
            return { ...s, content: res.data.summary };
          }
          if (type === 'bullets' && res.data.bullets && extra.itemIdx !== undefined) {
            const items = [...(s.items || [])];
            items[extra.itemIdx] = { ...items[extra.itemIdx], bullets: res.data.bullets };
            return { ...s, items };
          }
          if (type === 'skills' && res.data.skills) {
            return { ...s, items: res.data.skills };
          }
          return s;
        });
        return { ...prev, sections };
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'AI generation failed. Please try again.');
      logger.error('AI generate failed:', err);
    } finally {
      setAiLoading(null);
    }
  }, [resume.sections, targetJob]);

  const handleSave = useCallback(async () => {
    const plainText = resumeToPlainText(resume);
    if (!plainText || plainText.length < 50) {
      setError('Please add more content to your resume before saving.');
      return;
    }
    if (!resume.name) {
      setError('Please enter your name.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${apiRef.current}/resumes`, {
        title: `${resume.name}'s Resume`,
        content: plainText,
        target_job: targetJob,
        target_company: targetCompany,
      }, { withCredentials: true });
      setSuccess(`Resume saved! ATS Score: ${res.data.ats_score}/100`);
      setTimeout(() => navigate(`/resume/${res.data.resume_id}`), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save resume.');
      logger.error('Resume save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [resume, targetJob, targetCompany, navigate]);

  const handleExportPDF = useCallback(() => {
    const content = previewRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const doc = printWindow.document;
    doc.open();

    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const title = doc.createElement('title');
    title.textContent = resume.name || 'Resume';
    head.appendChild(title);

    const style = doc.createElement('style');
    style.textContent = [
      "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700;800&display=swap');",
      '* { margin: 0; padding: 0; box-sizing: border-box; }',
      "body { font-family: 'IBM Plex Sans', sans-serif; }",
      '@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }',
    ].join('\n');
    head.appendChild(style);
    html.appendChild(head);

    const body = doc.createElement('body');
    body.innerHTML = content.innerHTML;
    html.appendChild(body);

    doc.appendChild(html);
    doc.close();
    printWindow.onload = () => { printWindow.print(); };
  }, [resume.name]);

  const inputClass = "w-full px-3 py-2 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors bg-white";

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-16" data-testid="resume-builder-page">
      {/* Top Bar */}
      <div className="fixed top-16 left-0 md:left-16 lg:left-52 right-0 bg-white border-b border-[#e5e7eb] z-30 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-[#4b5563] hover:text-[#002fa7]" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-satoshi font-bold text-[#0f1115] text-sm">Resume Builder</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportPDF} className="btn-outline text-xs py-1.5 px-3 flex items-center gap-1.5" data-testid="export-pdf-btn">
            <Download size={14} /> PDF
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 disabled:opacity-50" data-testid="save-and-score-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {saving ? 'Saving...' : 'Save & Get ATS Score'}
          </button>
        </div>
      </div>

      <div className="pt-12 flex" style={{ height: 'calc(100vh - 64px)' }}>
        {/* Left: Editor */}
        <div className="w-full lg:w-1/2 overflow-y-auto p-4 pb-20" data-testid="editor-panel">
          {error && <div className="mb-3 p-3 bg-[#fee2e2] text-[#e11d48] text-sm">{error}</div>}
          {success && <div className="mb-3 p-3 bg-[#d1fae5] text-[#059669] text-sm font-semibold">{success}</div>}

          {/* Contact Info */}
          <div className="dashboard-card p-4 mb-3">
            <h2 className="text-xs uppercase tracking-wider font-bold text-[#6b7280] mb-3">Contact Information</h2>
            <div className="grid grid-cols-2 gap-2">
              <input value={resume.name} onChange={(e) => updateContactField('name', e.target.value)} placeholder="Full Name *" className={inputClass} data-testid="contact-name" />
              <input value={resume.email} onChange={(e) => updateContactField('email', e.target.value)} placeholder="Email" className={inputClass} data-testid="contact-email" />
              <input value={resume.phone} onChange={(e) => updateContactField('phone', e.target.value)} placeholder="Phone" className={inputClass} />
              <input value={resume.location} onChange={(e) => updateContactField('location', e.target.value)} placeholder="City, State" className={inputClass} />
              <input value={resume.linkedin} onChange={(e) => updateContactField('linkedin', e.target.value)} placeholder="LinkedIn URL" className={inputClass} />
              <input value={resume.website} onChange={(e) => updateContactField('website', e.target.value)} placeholder="Portfolio / Website" className={inputClass} />
            </div>
          </div>

          {/* Target Job (for ATS scoring) */}
          <div className="dashboard-card p-4 mb-3">
            <h2 className="text-xs uppercase tracking-wider font-bold text-[#6b7280] mb-3">Target Position</h2>
            <div className="grid grid-cols-2 gap-2">
              <input value={targetJob} onChange={(e) => setTargetJob(e.target.value)} placeholder="Target Job Title *" className={inputClass} data-testid="target-job" />
              <input value={targetCompany} onChange={(e) => setTargetCompany(e.target.value)} placeholder="Target Company (optional)" className={inputClass} />
            </div>
          </div>

          {/* Sections with DnD */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={resume.sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {resume.sections.map((section) => (
                <SectionEditor
                  key={section.id}
                  section={section}
                  onChange={(updated) => updateSection(section.id, updated)}
                  onRemove={() => removeSection(section.id)}
                  onAIGenerate={handleAIGenerate}
                  aiLoading={aiLoading}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Add Section */}
          <div className="relative">
            <button
              onClick={() => setShowAddSection(!showAddSection)}
              className="w-full py-3 border-2 border-dashed border-[#d1d5db] text-sm font-semibold text-[#6b7280] hover:text-[#002fa7] hover:border-[#002fa7] transition-colors flex items-center justify-center gap-2"
              data-testid="add-section-btn"
            >
              <Plus size={16} /> Add Section
            </button>
            {showAddSection && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e5e7eb] shadow-lg z-10 p-2 grid grid-cols-2 gap-1">
                {ADDABLE_SECTIONS.map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    onClick={() => addSection(type)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[#4b5563] hover:bg-[#f4f5f7] hover:text-[#002fa7] transition-colors text-left"
                    data-testid={`add-${type}`}
                  >
                    <Icon size={16} /> {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Preview */}
        <div className="hidden lg:block w-1/2 overflow-y-auto bg-[#e5e7eb] p-6" data-testid="preview-panel">
          {/* Template Selector */}
          <div className="flex items-center gap-2 mb-4">
            <Palette size={16} className="text-[#6b7280]" />
            <span className="text-xs uppercase tracking-wider font-bold text-[#6b7280]">Style:</span>
            {Object.values(TEMPLATE_STYLES).map((t) => (
              <button
                key={t.id}
                onClick={() => setTemplateId(t.id)}
                className={`text-xs px-2.5 py-1 font-semibold transition-colors ${
                  templateId === t.id ? 'bg-[#002fa7] text-white' : 'bg-white text-[#4b5563] hover:text-[#002fa7]'
                }`}
                data-testid={`style-${t.id}`}
                title={t.description}
              >
                {t.name}
              </button>
            ))}
          </div>

          <div ref={previewRef}>
            <ResumePreview resume={resume} templateId={templateId} />
          </div>
        </div>
      </div>
    </div>
  );
}
